import {Vector3} from 'three';
import {bunkProfile} from '../design/configurator/room/bunk-profile';
import {BED_OPTIONS} from '../design/configurator/room/beds';
import {wallCamera,roomFloorDepth,roomAnchorPosition,type RoomWalls} from './photo-walls';
import type {Placement} from './photo-placement';
import type {BasicUpgrades} from '../../../../shared/basicUpgrades';
/** Compare full-size layouts on the measured floor. Never move the camera or
 * shrink beds to make a photograph appear to accommodate a larger room. */
export function automaticRoomLayout(choices:number[],walls:RoomWalls,aspect:number,ceilingInches:number,upgrades:BasicUpgrades[]=[],layouts:('ernst'|'basic'|undefined)[]=[],preferredAxis?:'x'|'z'){
 const calibrated=wallCamera(walls,aspect);
 if(!calibrated)throw Error('Room perspective is unavailable');
 const camera=calibrated;
 const depth=roomFloorDepth(walls.corner,aspect,camera,ceilingInches);
 if(depth===null)throw Error('A visible ceiling and its height are needed for room scale');
 const origin=roomAnchorPosition(walls.corner,aspect,camera.focal,depth);
 const xAxis=new Vector3().setFromMatrixColumn(camera.basis,0),up=new Vector3().setFromMatrixColumn(camera.basis,1),zAxis=new Vector3().setFromMatrixColumn(camera.basis,2);
 const sizes=choices.map((choice,i)=>{
  if(layouts[i]==='ernst')return {x:178*.0254,z:82*.0254,boxes:[{min:[0,0,0],max:[178,92,82]}]};
  const bed=BED_OPTIONS[choice]??BED_OPTIONS[0],extra=upgrades[i]??{};
  const height=extra.towerSoffit||extra.puckUpper?ceilingInches:80;
  const {upperY}=bunkProfile(80),risers=Math.max(2,Math.round(upperY/12));
  const stairWidth=2.5+21+(risers-1)*12+(extra.stairDrawers ? .8 : 0);
  const boxes=[{min:[0,0,0],max:[bed.width,height,bed.depth]}];
  // Stairs occupy the foot end at platform height. Never score a fictional
  // ceiling-height box over their treads: that wrongly rejects usable photos.
  if(extra.stairs)boxes.push({min:[0,0,bed.depth-3],max:[stairWidth,upperY,bed.depth+21]});
  return {x:Math.max(bed.width,extra.stairs?stairWidth:0)*.0254,z:(bed.depth+(extra.stairs?21:0))*.0254,boxes};
 });
 const placement=(anchor:Vector3):Placement=>({x:(.5+camera.focal*anchor.x/(-anchor.z)/aspect)*100,y:(.5-camera.focal*anchor.y/(-anchor.z))*100,scale:100,turn:0,tilt:0});
 function candidate(axis:'x'|'z',selected=sizes){
  let offset=0,score=0;
  const anchors:Vector3[]=[];
  const placements=selected.map(size=>{
   const anchor=origin.clone().addScaledVector(axis==='x'?xAxis:zAxis,offset);offset+=size[axis]+.1016;
   anchors.push(anchor.clone());
   for(const box of size.boxes)for(const xIn of [box.min[0],box.max[0]])for(const yIn of [box.min[1],box.max[1]])for(const zIn of [box.min[2],box.max[2]]){
    const x=xIn*.0254,y=yIn*.0254,z=zIn*.0254;
    const p=anchor.clone().addScaledVector(xAxis,x).addScaledVector(up,y).addScaledVector(zAxis,z);
    // The camera must not sit inside or immediately against a model.
    if(p.z>=-.35){score=Infinity;continue;}
    score=Math.max(score,Math.abs(2*camera.focal*p.x/(-p.z)/aspect),Math.abs(2*camera.focal*p.y/(-p.z)));
   }
   return placement(anchor);
  });return {placements,anchors,score};
 }
 const alongX=candidate('x'),alongZ=candidate('z'),best=preferredAxis==='x'?alongX:preferredAxis==='z'?alongZ:alongX.score<=alongZ.score?alongX:alongZ;
 // Mild cropping remains optional. An extreme combined view is shown as
 // individual full-size previews instead of projecting a bunk into the camera.
 const separate=choices.length>1&&best.score>2;
 // Beyond 37.5% of the image outside an edge, request a clearer angle.
 // The customer may still show the crop or proceed at unchanged physical size.
 const individualScores=sizes.map(size=>candidate('x',[size]).score);
 // The orbit view can frame the complete layout even when the original photo
 // cannot. Keep real room anchors so off-camera bunks are not dropped or stacked.
 return {roomAnchors:best.anchors.map(anchor=>anchor.toArray()),frameExtent:separate?Math.max(...individualScores):best.score,placements:separate?choices.map(()=>placement(origin)):best.placements,separate,needsAnotherAngle:separate?individualScores.some(score=>score>1.75):best.score>1.75};
}
export function automaticBedPlacements(choices:number[],walls:RoomWalls,aspect:number,ceilingInches:number,upgrades:BasicUpgrades[]=[]){return automaticRoomLayout(choices,walls,aspect,ceilingInches,upgrades).placements;}


